import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthShell } from '@/components/auth/AuthShell.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { PasswordInput } from '@/components/ui/password-input.js';
import { resetPasswordWithBankAccountMutation } from '@/features/auth/api.js';
import { mapApiError } from '@/lib/api/error.js';
import { useBrandingStore } from '@/stores/branding.js';
import { TurnstileWidget } from '@/components/captcha/TurnstileWidget.js';
import { resetNeedsCaptcha } from '@/features/auth/reset-captcha.js';
import { useTranslation } from 'react-i18next';
import { PASSWORD_MIN_LENGTH } from '@/features/auth/password-policy.js';
import { BANK_ACCOUNT_NO_RE } from '@/features/auth/register-rules.js';

export const Route = createFileRoute('/_auth/forgot')({
  component: ForgotPage,
});

interface ForgotFormValues {
  identifier: string;
  accountNumber: string;
  newPassword: string;
  confirmNewPassword: string;
}

function ForgotPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const captcha = useBrandingStore((s) => s.branding?.captcha);
  const captchaOn = resetNeedsCaptcha(captcha);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);

  const mutation = useMutation({
    mutationFn: (value: ForgotFormValues) =>
      resetPasswordWithBankAccountMutation({
        identifier: value.identifier,
        accountNumber: value.accountNumber,
        newPassword: value.newPassword,
        ...(captchaToken ? { captchaToken } : {}),
      }),
    onSuccess: () => {
      toast.success(t('forgot.resetSuccess'));
      void navigate({ to: '/login' });
    },
    onError: (err) => {
      // Generic error only — no field-specific mapping. The backend
      // deliberately collapses "unknown identifier" and "wrong account
      // number" into one code so the UI must not re-introduce that signal.
      toast.error(mapApiError(err, t));
    },
  });

  const form = useForm<ForgotFormValues>({
    defaultValues: { identifier: '', accountNumber: '', newPassword: '', confirmNewPassword: '' },
    onSubmit: async ({ value }) => {
      await mutation.mutateAsync(value);
    },
  });

  const validateIdentifier = (value: string): string | undefined => {
    if (!value) return t('forgot.errors.identifierRequired');
    return undefined;
  };
  const validateAccountNumber = (value: string): string | undefined => {
    if (!value) return t('forgot.errors.accountNumberRequired');
    if (!BANK_ACCOUNT_NO_RE.test(value)) return t('forgot.errors.accountNumberInvalid');
    return undefined;
  };
  const validateNewPassword = (value: string): string | undefined => {
    if (!value) return t('forgot.errors.passwordRequired');
    if (value.length < PASSWORD_MIN_LENGTH) {
      return t('forgot.errors.passwordTooShort', { min: PASSWORD_MIN_LENGTH });
    }
    return undefined;
  };

  return (
    <AuthShell>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">{t('forgot.title')}</h1>
            <p className="text-[13px] text-[var(--text-secondary)] mt-1">
              {t('forgot.subtitle')}
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void form.handleSubmit();
            }}
            className="flex flex-col gap-3"
          >
            <form.Field
              name="identifier"
              validators={{ onChange: ({ value }) => validateIdentifier(value) }}
            >
              {(field) => {
                const error = field.state.meta.errors[0];
                return (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                      {t('forgot.identifier')}
                    </label>
                    <Input
                      placeholder={t('forgot.identifierPlaceholder')}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    {error && <span className="text-[11px] text-[var(--danger)]">{error}</span>}
                  </div>
                );
              }}
            </form.Field>

            <form.Field
              name="accountNumber"
              validators={{ onChange: ({ value }) => validateAccountNumber(value) }}
            >
              {(field) => {
                const error = field.state.meta.errors[0];
                return (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                      {t('forgot.accountNumber')}
                    </label>
                    <Input
                      placeholder={t('forgot.accountNumberPlaceholder')}
                      inputMode="numeric"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    {error && <span className="text-[11px] text-[var(--danger)]">{error}</span>}
                  </div>
                );
              }}
            </form.Field>

            <form.Field
              name="newPassword"
              validators={{ onChange: ({ value }) => validateNewPassword(value) }}
            >
              {(field) => {
                const error = field.state.meta.errors[0];
                return (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                      {t('forgot.newPassword')}
                    </label>
                    <PasswordInput
                      placeholder={t('forgot.newPassword')}
                      autoComplete="new-password"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    {error && <span className="text-[11px] text-[var(--danger)]">{error}</span>}
                  </div>
                );
              }}
            </form.Field>

            <form.Field
              name="confirmNewPassword"
              validators={{
                onChangeListenTo: ['newPassword'],
                onChange: ({ value }) => {
                  if (!value) return t('forgot.errors.confirmRequired');
                  if (value !== form.getFieldValue('newPassword')) return t('forgot.errors.passwordMismatch');
                  return undefined;
                },
              }}
            >
              {(field) => {
                const error = field.state.meta.errors[0];
                return (
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]">
                      {t('forgot.confirmNewPassword')}
                    </label>
                    <PasswordInput
                      placeholder={t('forgot.confirmNewPassword')}
                      autoComplete="new-password"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    {error && <span className="text-[11px] text-[var(--danger)]">{error}</span>}
                  </div>
                );
              }}
            </form.Field>

            {captchaOn && captcha?.siteKey && (
              <TurnstileWidget siteKey={captcha.siteKey} onSolve={setCaptchaToken} />
            )}

            <Button
              type="submit"
              size="lg"
              glow
              fullWidth
              disabled={mutation.isPending || (captchaOn && !captchaToken)}
            >
              {mutation.isPending ? t('forgot.submitting') : t('forgot.submit')}
            </Button>
            <Link to="/login">
              <Button type="button" variant="ghost" fullWidth>
                {t('forgot.backToLogin')}
              </Button>
            </Link>
          </form>
    </AuthShell>
  );
}
